<template>
  <SFFooterSection
    :title="$t('footer.follow_us')"
    content-class="flex items-center gap-4"
  >
    <template v-if="followUs && followUs.length > 0">
      <a
        v-for="social in followUs"
        :key="social.id"
        :href="getSocialLink(social)"
        :title="social.name"
        :target="getSocialTarget(social)"
        rel="noopener noreferrer"
        class="flex size-9 items-center justify-center rounded-full transition-opacity hover:opacity-80"
      >
        <!-- Display social icon using CDN URL for static assets -->
        <img
          v-if="getSocialIconHash(social)"
          :src="getSocialIconUrl(social)"
          :alt="social.name"
          class="size-9 object-contain"
          loading="lazy"
        />
        <span v-else class="text-xs text-white">
          {{ social.name.substring(0, 2).toUpperCase() }}
        </span>
      </a>
    </template>
    <template v-else>
      <!-- Fallback: show placeholder social media icons -->
      <div
        v-for="n in 6"
        :key="`soc-${n}`"
        class="size-8 rounded-full bg-gray-600"
      />
    </template>
  </SFFooterSection>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue'
import type { NavigationTreeItem } from '@scayle/storefront-nuxt'
import { useRouteHelpers } from '~/composables/useRouteHelpers'
import { useRuntimeConfig } from '#app'

const SFFooterSection = defineAsyncComponent(
  () => import('./SFFooterSection.vue'),
)

defineOptions({
  name: 'SFFooterSocialSection',
})

const { buildNavigationTreeItemRoute } = useRouteHelpers()
const config = useRuntimeConfig()

defineProps<{
  followUs?: NavigationTreeItem[]
}>()

const getSocialIconHash = (social: NavigationTreeItem): string | undefined => {
  // Handle NavigationTreeItem assets
  if ('assets' in social && social.assets) {
    const assets = Object.values(social.assets)
    const assetPath = assets[0] as string
    return assetPath
  }

  return undefined
}

const getSocialIconUrl = (social: NavigationTreeItem): string | undefined => {
  const iconHash = getSocialIconHash(social)
  if (!iconHash) {
    return undefined
  }

  // Use CDN URL instead of Cloudinary for the same Contentful path
  const cdnUrl = config.public.cdnUrl || 'https://snipes-test.cdn.scayle.cloud'

  // Keep the original path from Contentful, just replace the base URL
  return `${cdnUrl}/${iconHash}`
}

const getSocialLink = (social: NavigationTreeItem): string => {
  const routeResult = buildNavigationTreeItemRoute(social)

  if (!routeResult) {
    return '#'
  }

  const { route } = routeResult

  if (typeof route === 'string') {
    return route
  }

  if (route && typeof route === 'object' && 'path' in route) {
    return route.path || '#'
  }

  return '#'
}

const getSocialTarget = (social: NavigationTreeItem): string => {
  const routeResult = buildNavigationTreeItemRoute(social)
  return routeResult?.openInNew ? '_blank' : '_self'
}
</script>
